import React from "react";
import { Switch } from "react-router-dom";

import { Guid } from "guid-typescript";

import {
  DictionaryIcon,
  TranslationsIcon,
  SecurityRolesIcon,
  SecurityRoleMembersIcon,
  PlexMessageIdManagerIcon,
  CustomerTranslationManagerIcon,
  PlexTranslationManagerIcon,
  ReleaseManagementIcon
} from "@plex/icons";

import { AppRoute } from "AppRoute";
import { Roles, Tenants } from "config";
import { commonMessages } from "infrastructure/localization";
import { EmptyLayout } from "ui/app/layout";
import { IAppRouteProps, IRouteGroupConfig } from "ui/app/navigation/IAppRouteProps";
import { Circle } from "ui/components";
import {
  SignIn,
  SignedIn,
  NotFound,
  TranslationsHistory,
  TermTranslations,
  AppLanguageSwitch,
  TranslationsManager,
  TranslationMessagesManager,
  PlexTranslationMessagesManager,
  TermDictionary,
  Messages,
  MessageDetails,
  AllMessageTranslations,
  SecurityRoles,
  SecurityRoleMembers,
  CreateRelease,
  ReleasesManager,
  ReleaseDetails
} from "ui/screens";
import { glossaryMessages } from "ui/screens/plex-dictionary";
import { releaseMessages } from "ui/screens/release";
import { securityMessages } from "ui/screens/security";
import { displayMessages } from "ui/screens/translation-managers";

export type AppRouteConfig = IAppRouteProps;

const termsGroup: IRouteGroupConfig = {
  id: Guid.raw(),
  title: glossaryMessages.title("termsGroup")
};

const messagesGroup: IRouteGroupConfig = {
  id: Guid.raw(),
  title: displayMessages.title("messagesGroup")
};

const securityGroup: IRouteGroupConfig = {
  id: Guid.raw(),
  title: securityMessages.title("securityGroup")
};

const releasesGroup: IRouteGroupConfig = {
  id: Guid.raw(),
  title: releaseMessages.title("releasesGroup")
};

const iconSize = {
  width: 20,
  height: 20
};

export const routes: IAppRouteProps[] = [
  {
    id: Guid.raw(),
    icon: (
      <Circle className={"term-dictionary-icon"}>
        <DictionaryIcon style={iconSize} />
      </Circle>
    ),
    exact: true,
    path: "/",
    title: glossaryMessages.title("termDictionary"),
    showInMainMenu: true,
    component: TermDictionary,
    group: termsGroup
  },
  {
    id: Guid.raw(),
    exact: true,
    icon: (
      <Circle className={"term-translations-manager-icon"}>
        <TranslationsIcon style={iconSize} />
      </Circle>
    ),
    path: "/terms/translations-manager",
    title: glossaryMessages.title("termTranslationsManager"),
    requiredAuthorizationData: [
      {
        tenants: [Tenants.Plex],
        roles: [Roles.Translator, Roles.PlexLocalizationManager]
      }
    ],
    component: TranslationsManager,
    showInMainMenu: true,
    group: termsGroup
  },
  {
    id: Guid.raw(),
    exact: true,
    icon: (
      <Circle className={"security-roles-icon"}>
        <SecurityRolesIcon style={iconSize} />
      </Circle>
    ),
    path: "/security/security-roles",
    title: securityMessages.title("securityRoles"),
    requiredAuthorizationData: [
      {
        tenants: [],
        roles: [Roles.SecurityAdmin, Roles.PlexSecurityAdmin]
      }
    ],
    component: SecurityRoles,
    showInMainMenu: true,
    group: securityGroup
  },
  {
    id: Guid.raw(),
    exact: true,
    icon: (
      <Circle className={"security-role-members-icon"}>
        <SecurityRoleMembersIcon style={iconSize} />
      </Circle>
    ),
    path: ["/security/security-role-members", "/security/security-role-members/:roleId?"],
    title: securityMessages.title("securityRoleMembers"),
    requiredAuthorizationData: [
      {
        tenants: [],
        roles: [Roles.SecurityAdmin, Roles.PlexSecurityAdmin]
      }
    ],
    component: SecurityRoleMembers,
    showInMainMenu: true,
    group: securityGroup
  },
  {
    id: Guid.raw(),
    exact: true,
    path: "/message-details/:messageId?",
    title: displayMessages.title("messageDetails"),
    component: MessageDetails
  },
  {
    id: Guid.raw(),
    exact: true,
    path: "/terms/:termId/translations/:status?",
    title: glossaryMessages.title("termTranslations"),
    component: TermTranslations
  },
  {
    id: Guid.raw(),
    exact: true,
    path: "/terms/:termId/translation-history",
    title: glossaryMessages.title("termTranslationsHistory"),
    component: TranslationsHistory
  },
  {
    id: Guid.raw(),
    exact: true,
    icon: (
      <Circle className={"plex-icon-plex-message-id-manager"}>
        <PlexMessageIdManagerIcon style={iconSize} />
      </Circle>
    ),
    path: "/messages",
    title: displayMessages.title("messageManager"),
    component: Messages,
    showInMainMenu: true,
    group: messagesGroup
  },
  {
    id: Guid.raw(),
    exact: true,
    path: "/messages/:messageId/translations",
    title: displayMessages.title("messageTranslations"),
    component: AllMessageTranslations
  },
  {
    id: Guid.raw(),
    exact: true,
    icon: (
      <Circle className={"plex-icon-customer-translation-manager"}>
        <CustomerTranslationManagerIcon style={iconSize} />
      </Circle>
    ),
    path: "/messages/translation",
    title: displayMessages.title("translationManager"),
    requiredAuthorizationData: [
      {
        tenants: [],
        roles: [Roles.LocalizationManager, Roles.PlexLocalizationManager]
      }
    ],
    component: TranslationMessagesManager,
    showInMainMenu: true,
    group: messagesGroup
  },
  {
    id: Guid.raw(),
    exact: true,
    icon: (
      <Circle className={"plex-icon-plex-translation-manager"}>
        <PlexTranslationManagerIcon style={iconSize} />
      </Circle>
    ),
    path: "/messages/plex-translation",
    title: displayMessages.title("plexTranslationsManager"),
    requiredAuthorizationData: [
      {
        tenants: [],
        roles: [Roles.PlexLocalizationManager, Roles.LocalizationManager, Roles.Translator]
      }
    ],
    component: PlexTranslationMessagesManager,
    showInMainMenu: true,
    group: messagesGroup
  },
  {
    id: Guid.raw(),
    exact: true,
    icon: (
      <Circle className={"plex-icon-releases-manager"}>
        <ReleaseManagementIcon style={iconSize} />
      </Circle>
    ),
    path: "/releases",
    title: releaseMessages.title("releasesManager"),
    requiredAuthorizationData: [
      {
        tenants: [Tenants.Plex],
        roles: [Roles.PlexReleaseManager]
      }
    ],
    component: ReleasesManager,
    showInMainMenu: true,
    group: releasesGroup
  },
  {
    id: Guid.raw(),
    exact: true,
    path: "/releases/create",
    title: releaseMessages.title("createRelease"),
    requiredAuthorizationData: [
      {
        tenants: [Tenants.Plex],
        roles: [Roles.PlexReleaseManager]
      }
    ],
    group: releasesGroup,
    component: CreateRelease
  },
  {
    id: Guid.raw(),
    exact: true,
    path: "/releases/:releaseId/details",
    title: releaseMessages.title("releaseDetails"),
    requiredAuthorizationData: [
      {
        tenants: [Tenants.Plex],
        roles: [Roles.PlexReleaseManager]
      }
    ],
    component: ReleaseDetails
  },
  {
    id: Guid.raw(),
    exact: true,
    path: "/signin",
    title: commonMessages.title("signIn"),
    component: SignIn,
    layout: EmptyLayout
  },
  {
    id: Guid.raw(),
    exact: true,
    path: "/choose-locale",
    title: commonMessages.title("chooseLocale"),
    component: AppLanguageSwitch
  },
  {
    id: Guid.raw(),
    exact: true,
    path: "/signedin",
    title: commonMessages.title("signedIn"),
    component: SignedIn,
    layout: EmptyLayout,
    allowAnonymous: true
  },
  {
    id: Guid.raw(),
    exact: true,
    title: commonMessages.title("notFoundScreen"),
    component: NotFound
  }
];

export const AppRoutes: React.FC = () => {
  return (
    <Switch>
      {routes.map((route, i) => (
        <AppRoute key={i} {...route} />
      ))}
    </Switch>
  );
};
